import { useModalState } from "@clipboard-health/ui-components";
import { getTheme } from "@clipboard-health/ui-theme";
import { Box, CssBaseline, ThemeProvider } from "@mui/material";
import { APP_V2_APP_EVENTS, APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useMemo } from "react";
import { Redirect } from "react-router-dom";

import { AnimatedInsightWrapper } from "./AnimatedInsightWrapper";
import { useGetCbhWrappedInsights } from "./api/useGetCbhWrappedInsights";
import { CbhWrappedHeader } from "./Header";
import { EndContent } from "./Insight/End/Content";
import { isKnownInsight } from "./Insight/isKnownInsight";
import { KnownInsightContentContainer } from "./Insight/KnownInsightContentContainer";
import { StartInsightContent } from "./Insight/Start/Content";
import { type KnownInsightType } from "./Insight/types";
import { LoadingInsightsState } from "./LoadingStateContent";
import { ShareInsightButton } from "./ShareButton";
import { ShareNudgeBottomSheet } from "./ShareNudgeBottomSheet";
import { useInsightsNavigation } from "./useInsightsNavigation";
import { useShareInsight } from "./useShareInsight";

export function CbhWrappedPage() {
  const worker = useDefinedWorker();

  const {
    data: wrappedInsights,
    isLoading: isWrappedInsightsLoading,
    isError: isWrappedInsightsError,
  } = useGetCbhWrappedInsights();

  const knownInsights = useMemo(
    () =>
      wrappedInsights?.data.filter((insight) => isKnownInsight(insight.attributes.insightType)) ??
      [],
    [wrappedInsights]
  );

  const nudgeShareModalState = useModalState("closed");

  const { currentIndex, lastIndex, currentInsight, isLast, goNext, goPrevious } =
    useInsightsNavigation({
      insights: knownInsights,
    });

  const { mutateAsync: shareInsight, isLoading: isSharing } = useShareInsight();

  useLogEffect(
    APP_V2_USER_EVENTS.CBH_WRAPPED_PAGE_VIEWED,
    {
      workerId: worker.userId,
      totalInsights: knownInsights.length,
      insightTypes: knownInsights.map((insight) => insight.attributes.insightType),
    },
    { enabled: knownInsights.length > 0 }
  );

  const handleNext = () => {
    logEvent(APP_V2_USER_EVENTS.CBH_WRAPPED_NEXT_CLICKED, {
      workerId: worker.userId,
      fromIndex: currentIndex,
      toIndex: currentIndex + 1,
      fromInsightType: currentInsight?.attributes.insightType,
    });

    goNext();
  };

  const handlePrevious = () => {
    logEvent(APP_V2_USER_EVENTS.CBH_WRAPPED_PREVIOUS_CLICKED, {
      workerId: worker.userId,
      fromIndex: currentIndex,
      toIndex: currentIndex - 1,
      fromInsightType: currentInsight?.attributes.insightType,
    });
    goPrevious();
  };

  const handleShare = async (
    insightType: KnownInsightType | undefined,
    source: "direct" | "bottomSheet"
  ) => {
    logEvent(APP_V2_USER_EVENTS.CBH_WRAPPED_INSIGHT_SHARE_STARTED, {
      workerId: worker.userId,
      insightType,
      source,
      currentIndex,
    });

    const { shared, error } = await shareInsight(insightType);

    if (shared) {
      logEvent(APP_V2_APP_EVENTS.CBH_WRAPPED_INSIGHT_SHARE_SUCCESS, {
        workerId: worker.userId,
        insightType,
        source,
        currentIndex,
      });
    } else {
      logEvent(APP_V2_APP_EVENTS.CBH_WRAPPED_INSIGHT_SHARE_FAILURE, {
        workerId: worker.userId,
        insightType,
        source,
        currentIndex,
        error,
      });
    }
  };

  if (isWrappedInsightsError || (!isWrappedInsightsLoading && knownInsights.length === 0)) {
    return <Redirect to="/" />;
  }

  return (
    <ThemeProvider theme={getTheme()}>
      <CssBaseline />
      <Box sx={{ height: "100vh", position: "relative" }}>
        {knownInsights.length > 0 && (
          <CbhWrappedHeader
            currentStep={currentIndex}
            lastInsightIndex={lastIndex}
            onNext={handleNext}
            onPrevious={handlePrevious}
          />
        )}

        <AnimatedInsightWrapper visible={isWrappedInsightsLoading} insightType="loading">
          <LoadingInsightsState />
        </AnimatedInsightWrapper>

        <AnimatedInsightWrapper
          visible={!isWrappedInsightsLoading && currentIndex === 0}
          insightType="start"
          onClick={handleNext}
        >
          <StartInsightContent onNext={handleNext} />
        </AnimatedInsightWrapper>

        {knownInsights.map((insight) => (
          <KnownInsightContentContainer
            key={insight.id}
            insight={insight}
            visible={currentInsight?.attributes.insightType === insight.attributes.insightType}
            onClick={handleNext}
          />
        ))}

        <AnimatedInsightWrapper visible={isLast} insightType="end">
          <EndContent
            onFinished={() => {
              logEvent(APP_V2_USER_EVENTS.CBH_WRAPPED_SHARE_NUDGE_VIEWED, {
                workerId: worker.userId,
                availableInsights: knownInsights.map((insight) => insight.attributes.insightType),
              });

              nudgeShareModalState.openModal();
            }}
          />
        </AnimatedInsightWrapper>

        {knownInsights.length > 0 && currentIndex > 0 && currentIndex < lastIndex && (
          <ShareInsightButton
            isSharing={isSharing}
            handleShare={async () => {
              await handleShare(currentInsight?.attributes.insightType, "direct");
            }}
          />
        )}
      </Box>

      <ShareNudgeBottomSheet
        modalState={nudgeShareModalState}
        knownInsights={knownInsights}
        handleShare={async (insightType) => {
          await handleShare(insightType, "bottomSheet");
        }}
      />
    </ThemeProvider>
  );
}
